import React, { FC, useContext, memo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { last, isEqual, get } from 'lodash';
import { usePrevious } from 'react-use';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import QuestionComponent from '../Question';
import AddQuestionInMiddle from '../AddQuestionInMiddle';
import List, { DragResponse } from 'vibo-ui/List';
import { EventContext } from 'components/context/EventContext';
import {
  TimelineQuestionsContext,
  QuestionActionsContext,
} from 'components/events/Timeline/TimelineQuestionsBlock/TimelineQuestionsContext';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import { EMPTY_MUTATION } from 'graphql/mutations/comon';
import { updateEventProgress } from 'graphql/cache/events';
import { useTargetingQuestion } from 'graphql/hooks/questions';
import { updateSectionProgress } from 'graphql/cache/sections';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { Modals } from 'types/enums';
import { DndTypes } from 'components/common/Dnd';
import { QuestionsProps } from './interfaces';

import useStyles from './style';

const Questions: FC<QuestionsProps> = ({ onAddQuestion }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [currentQuestions, setCurrentQuestions] = useState<Question[]>([]);

  const { isEventDj, canEditSection } = useContext(EventContext);
  const { questions } = useContext(TimelineQuestionsContext);

  const {
    variables,
    questions: { mutations },
  } = useTimeline<AllTimelineGqlOptions>();

  const { closeModal } = useModal();

  const closeCreateQuestionModal = useCallback(() => closeModal(Modals.createQuestion), []);

  const { id: eventId = '', sectionId = '' } = useParams<keyof EventPageRouteParams>();

  const prevQuestions = usePrevious(usePrevious(questions));
  const prevSectionId = usePrevious(sectionId);

  const { clearQuestionTarget } = useTargetingQuestion();

  const [reorderQuestions] = useMutation(mutations.reorder.mutation, {
    onError,
    refetchQueries: mutations.reorder.refetchQ,
  });

  const [answerQuestion, { loading: isAnswering }] = useMutation<Question>(
    mutations?.answer?.mutation ?? EMPTY_MUTATION,
    {
      onCompleted: data => {
        const res: Maybe<TypeQuestionProgress> = get(data, 'answerEventSectionQuestionV2');

        if (res) {
          updateSectionProgress(sectionId, res);
          updateEventProgress(eventId, res);
        }
      },
      onError,
    }
  );
  const [updateQuestion] = useMutation<Question>(mutations.update.mutation, {
    onCompleted: () => {
      toastNotify({ text: t('changesSavedSuccessfully') });
      closeCreateQuestionModal();
    },
    onError,
    refetchQueries: mutations.update.refetchQ,
  });
  const [cloneQuestion] = useMutation<Question>(mutations.clone.mutation, {
    onCompleted: data => {
      const res = Object.values(data)[0] as TypeQuestionProgress;

      updateEventProgress(eventId, res);
    },
    onError,
  });
  const [deleteQuestion, { loading: deleteQuestionLoading }] = useMutation(
    mutations.delete.mutation,
    {
      onCompleted: data => {
        const res: Maybe<TypeQuestionProgress> = get(data, 'deleteEventSectionQuestion');

        if (res) {
          updateSectionProgress(sectionId, res);
          updateEventProgress(eventId, res);
        }
      },
      onError,
    }
  );

  const handleAnswerQuestion = (questionId: string) => (payload: object) => {
    answerQuestion({
      variables: {
        ...variables,
        questionId,
        payload,
      },
    });
  };
  const handleUpdateQuestion = (questionId: string) => (payload: object, refetch?: string[]) => {
    updateQuestion({
      variables: {
        ...variables,
        questionId,
        payload,
      },
      refetchQueries: refetch || mutations.update.refetchQ,
    });
  };

  const handleCloneQuestion = (questionId: string) =>
    cloneQuestion({
      variables: {
        ...variables,
        questionId,
      },
      refetchQueries: mutations.clone.refetchQ,
    });

  const handleDeleteQuestion = (questionId: string) => {
    setCurrentQuestions(currentQuestions.filter(({ _id }) => _id !== questionId));
    deleteQuestion({
      variables: {
        ...variables,
        questionId,
      },
      refetchQueries: mutations.delete.refetchQ,
    });
  };

  const handleDragChange = (newData: Question[], { removedIndex, addedIndex }: DragResponse) => {
    if (!isEqual(currentQuestions, newData)) {
      setCurrentQuestions(newData);
      reorderQuestions({
        variables: {
          ...variables,
          sourceQuestionId: currentQuestions[removedIndex]._id,
          targetQuestionId: addedIndex < 0 ? null : currentQuestions[addedIndex]._id,
        },
      });
    }
  };

  const enableReorderQuestions = isEventDj && canEditSection;

  useEffect(() => {
    if (prevSectionId && prevSectionId !== sectionId) {
      setCurrentQuestions([]);
    }
  }, [sectionId]);

  useEffect(() => {
    closeCreateQuestionModal();
  }, []);

  useEffect(() => {
    setCurrentQuestions(questions);
  }, [questions]);

  useEffect(() => {
    if (
      prevQuestions &&
      prevQuestions?.length > 0 &&
      questions.length > prevQuestions?.length &&
      prevSectionId === sectionId
    ) {
      const lastQuestion = last(questions);
      if (lastQuestion) {
        const callbackTimer = setTimeout(() => {
          onAddQuestion?.();
          clearTimeout(callbackTimer);
        });
      }
    }
  }, [questions.length, prevQuestions]);

  useEffect(() => {
    return () => clearQuestionTarget();
  }, []);

  return (
    <QuestionActionsContext.Provider
      value={{
        deleteQuestion: handleDeleteQuestion,
        cloneQuestion: handleCloneQuestion,
        answerQuestion: handleAnswerQuestion,
        updateQuestion: handleUpdateQuestion,
        deleteQuestionLoading,
      }}
    >
      <AddQuestionInMiddle questionId={null} />
      <List<Question>
        data={currentQuestions}
        onDropCallback={handleDragChange}
        className={classNames(classes.questions, {
          isAnswering,
          withMinHeight: !currentQuestions.length,
        })}
        renderItem={question => (
          <QuestionComponent question={question} key={`question-${sectionId}-${question._id}`} />
        )}
        draggable={enableReorderQuestions}
        dndType={DndTypes.TIMELINE_QUESTION}
        scrollId="timeline-blocks"
        dragByHandler
      />
    </QuestionActionsContext.Provider>
  );
};

export default memo(Questions);
